import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { useForm } from 'react-hook-form';
import { useMutation } from '@apollo/client';

import { bookingShape, userShape } from 'constants/propTypes';
import hotelPropType from 'constants/propTypes/hotel';
import { getBookingJourneysURL, getPendingPaymentURL, getRefundMethodsURL } from 'utils/urls';
import { calculateMilesUsed, getBookingAttachedLoyaltyAccount, loyaltyPointsDifferencesCreationAndCancelLegs } from 'utils/booking';
import { productFamilyCodes } from 'types/productFamilyCodes';
import { REVERT_CHANGE } from 'store/mutations';

import { Column, Page, Row } from 'components/shared/Layout';
import LoyaltyLoggedOutModal from 'components/shared/LoyaltyLoggedOutModal';
import CostOfCancellation from 'components/ui/ReviewBookingSummary/SegmentSummary/CostOfCancellation';
import ReviewBookingSummary from 'components/ui/ReviewBookingSummary';

import LoyaltyMismatchModal from '../../../components/shared/LoyaltyMismatchModal';

import RefundsActions from './RefundsActions';
import { handleTotalPriceToBePaid } from './Refunds.util';
import CancelExtras from './CancelExtras';
import AlertBox from 'components/ui/AlertBox';
import { useMemo } from 'react';
import { getIsPackageBookingOnly } from 'components/ui/Payments/Payments.utils';

const Refunds = ({
  overrideFees,
  booking,
  hotels,
  user,
  currency,
  onCurrencyChange,
  membershipNumber,
  hasTierBenefitUpgradeProducts,
  userTierBenefitUpgrades = [],
  hasBusinessBenefitUpgradeProducts,
  userBusinessBenefitUpgrades = []
}) => {
  const {
    totalPriceToBePaid,
    reference,
    pendingCreationLegs,
    pendingCancellationLegs,
    additionalDetails = [],
    isLoyalty,
    allHotels = [],
    hasBeenSoldViaThirdPartySalesChannel
  } = booking;
  const totalPoints = Math.abs(loyaltyPointsDifferencesCreationAndCancelLegs(booking));
  const milesUsed = calculateMilesUsed(booking);

  const [hasLoyaltyAccountError, setLoyaltyAccountError] = useState(false);
  const [hasLoyaltyAccountMismatchError, setLoyaltyAccountMismatchError] = useState(false);

  const isLoyaltyLoggedOut =
    (booking.productFamilies?.map(({ familyGroup }) => familyGroup).includes(productFamilyCodes.EUROSTAR_LOYALTY) ||
      hasTierBenefitUpgradeProducts ||
      hasBusinessBenefitUpgradeProducts) &&
    !membershipNumber;

  const shouldShowTierBenefitWarning = useMemo(() => {
    if (!hasTierBenefitUpgradeProducts) {
      return null;
    } else if (userTierBenefitUpgrades.length === 0) {
      return 'wrongMembershipAccount';
    } else if (userTierBenefitUpgrades.length > 0) {
      return 'automaticallyCreditBackToAccount';
    } else {
      return 'notAutomaticallyCreditBackToAccount';
    }
  }, [hasTierBenefitUpgradeProducts, userTierBenefitUpgrades.length]);

  const shouldShowBusinessBenefitWarning = useMemo(() => {
    if (!hasBusinessBenefitUpgradeProducts) {
      return null;
    } else if (userBusinessBenefitUpgrades.length === 0) {
      return 'wrongMembershipAccount';
    } else if (userBusinessBenefitUpgrades.length > 0) {
      return 'automaticallyCreditBackToAccount';
    } else {
      return 'notAutomaticallyCreditBackToAccount';
    }
  }, [hasBusinessBenefitUpgradeProducts, userBusinessBenefitUpgrades.length]);

  const bookingAttachedLoyaltyAccount = getBookingAttachedLoyaltyAccount(booking);

  const isLoyaltyMismatch =
    booking.productFamilies?.map(({ familyGroup }) => familyGroup).includes(productFamilyCodes.EUROSTAR_LOYALTY) &&
    membershipNumber &&
    bookingAttachedLoyaltyAccount &&
    membershipNumber !== bookingAttachedLoyaltyAccount;

  const [revertChange] = useMutation(REVERT_CHANGE, {
    variables: {
      bookingReference: reference
    }
  });

  const { handleSubmit, formState } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange'
  });

  const { errors } = formState;

  const onSubmit = (callback) => {
    const submit = handleSubmit(callback);

    return (...args) => {
      if (isLoyaltyLoggedOut) {
        return setLoyaltyAccountError(true);
      }

      if (isLoyaltyMismatch) {
        return setLoyaltyAccountMismatchError(true);
      }

      return submit(...args);
    };
  };

  const handleCloseModal = () => setLoyaltyAccountError(!hasLoyaltyAccountError);
  const handleCloseMismatchModal = () => setLoyaltyAccountMismatchError(!hasLoyaltyAccountMismatchError);

  const pendingCreationLeg = pendingCreationLegs && pendingCreationLegs[0];
  const journeysRoute = getBookingJourneysURL(reference, membershipNumber);

  const markBookingPaymentAsProcessed = totalPriceToBePaid >= 0;

  const hotelFees = [...booking.hotelCancellationFees, ...booking.hotelMarginCancellationFees];
  const hasHotelFees = hotelFees.reduce((acc, fee) => acc + fee.price, 0) > 0;

  const ctaRoute = handleTotalPriceToBePaid({
    amount: totalPriceToBePaid,
    moreThanValue: getPendingPaymentURL(reference, null, membershipNumber),
    lessThanValue: getRefundMethodsURL(reference, null, membershipNumber),
    defaultValue: journeysRoute
  });

  const ctaLabelID = handleTotalPriceToBePaid({
    amount: totalPriceToBePaid,
    moreThanValue: 'refunds.takePayment',
    lessThanValue: 'refunds.selectRefundsMethods',
    defaultValue: 'refunds.confirmCancellation'
  });

  const productType = getIsPackageBookingOnly(additionalDetails);

  if (!pendingCreationLeg && !pendingCancellationLegs[0]) {
    return <Redirect to={journeysRoute} />;
  }

  const { hotelCancellationFees, hotelMarginCancellationFees, trainMarginCancellationFees } = booking;

  const cancelHotelInJacTravel =
    hotels.some((hotel) => hotel.cancelled && hotel.provisional) && booking.externalBookingReferences.some((ref) => ref.source === 'JAC');

  return (
    <Page>
      <LoyaltyLoggedOutModal isOpen={hasLoyaltyAccountError} onClose={handleCloseModal} onCtaClick={revertChange} />
      <LoyaltyMismatchModal isOpen={hasLoyaltyAccountMismatchError} onClose={handleCloseMismatchModal} onCtaClick={revertChange} />

      <Row span={14} start={2} noGutter>
        <Column start={1} span={10}>
          <h1>
            <FormattedMessage id="refunds.title" />
          </h1>
        </Column>
      </Row>

      <Row span={14} start={2}>
        <ReviewBookingSummary
          isRefundPath={true}
          title="refunds.cancellationSummary"
          booking={booking}
          hotels={hotels}
          currency={currency}
          getLoyaltyPointsDifference={totalPoints}
          cancelExtras={
            <CancelExtras
              hotels={hotels}
              bookingReference={booking.reference}
              bookingStatus={booking.status}
              errors={errors}
              isPackage={!!productType}
            />
          }
          showToggle
        />

        {user && (
          <CostOfCancellation
            currency={currency}
            reference={reference}
            hotels={hotels}
            trainMarginCancellationFees={trainMarginCancellationFees}
            hotelCancellationFees={hotelCancellationFees}
            hotelMarginCancellationFees={hotelMarginCancellationFees}
            overrideFees={overrideFees}
            totalPriceToBePaid={totalPriceToBePaid}
            totalPoints={totalPoints}
            pendingCancellationLegs={pendingCancellationLegs}
            pendingCreationLegs={pendingCreationLegs}
            canOverrideFees={user.canOverrideFees}
            isBeingRepriced={booking.isBeingRepriced}
            hasHotelFees={hasHotelFees}
            isLoyalty={isLoyalty}
            user={user}
            customerEmail={booking.customer?.email}
          />
        )}

        {hasBeenSoldViaThirdPartySalesChannel && totalPriceToBePaid < 0 && (
          <AlertBox variant={AlertBox.variants.WARNING}>
            <FormattedMessage id="journeySummary.thirdPartyBooking" />
          </AlertBox>
        )}

        {shouldShowTierBenefitWarning && (
          <AlertBox variant={AlertBox.variants.WARNING}>
            <FormattedMessage
              id={`tierBenefirUpgrade.${shouldShowTierBenefitWarning}`}
              values={{ amount: userTierBenefitUpgrades.length, account: membershipNumber }}
            />
          </AlertBox>
        )}

        {shouldShowBusinessBenefitWarning && (
          <AlertBox variant={AlertBox.variants.WARNING}>
            <FormattedMessage
              id={`businessBenefitUpgrade.${shouldShowBusinessBenefitWarning}`}
              values={{ amount: userBusinessBenefitUpgrades.length, account: membershipNumber }}
            />
          </AlertBox>
        )}

        <RefundsActions
          currency={currency}
          reference={reference}
          label={<FormattedMessage id={ctaLabelID} />}
          ctaRoute={ctaRoute}
          defaultRoute={journeysRoute}
          skipBookingConfirmation={totalPriceToBePaid > 0}
          markPaymentAsProcessed={markBookingPaymentAsProcessed}
          handleSubmit={onSubmit}
          cancelHotelInJacTravel={cancelHotelInJacTravel}
          membershipNumber={membershipNumber}
          totalPoints={totalPoints}
          milesUsed={milesUsed}
          hotels={allHotels}
          userTierBenefitUpgrades={userTierBenefitUpgrades}
          userBusinessBenefitUpgrades={userBusinessBenefitUpgrades}
        />
      </Row>
    </Page>
  );
};

Refunds.propTypes = {
  overrideFees: PropTypes.func,
  currency: PropTypes.string,
  booking: bookingShape.isRequired,
  hotels: PropTypes.arrayOf(hotelPropType),
  user: userShape,
  onCurrencyChange: PropTypes.func.isRequired,
  membershipNumber: PropTypes.string,
  hasTierBenefitUpgradeProducts: PropTypes.bool,
  hasBusinessBenefitUpgradeProducts: PropTypes.bool,
  userTierBenefitUpgrades: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      benefitStatus: PropTypes.string,
      benefitName: PropTypes.string,
      transactionJournal: PropTypes.string,
      startDate: PropTypes.string,
      endDate: PropTypes.string,
      cancellationReason: PropTypes.oneOfType([PropTypes.string, PropTypes.objectOf(null)]),
      bookingReference: PropTypes.string,
      benefitRedemptionDate: PropTypes.string
    })
  ),
  userBusinessBenefitUpgrades: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      benefitStatus: PropTypes.string,
      benefitName: PropTypes.string,
      benefitTypeName: PropTypes.string,
      transactionJournal: PropTypes.string,
      startDate: PropTypes.string,
      endDate: PropTypes.string,
      reasonForCancellation: PropTypes.oneOfType([PropTypes.string, PropTypes.objectOf(null)]),
      pnr: PropTypes.string,
      benefitRedemptionDate: PropTypes.string
    })
  )
};

export default Refunds;
